import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSnackbar } from '../redux/reducers/optionsReducers';
import { 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  X,
  Zap,
  Bell,
  Star,
  Heart
} from 'lucide-react';

const SnackContainer = () => {

  const snackbar = useSelector((state) => state.options.snackbar);
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      dispatch(toggleSnackbar({
        open: false,
        message: '',
        description: '',
        severity: 'success',
        position: 'top-center'
      }));
      setIsExiting(false);
      setIsVisible(false);
    }, 400);
  };

  useEffect(() => {
    if (snackbar?.open) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        handleClose();
      }, snackbar?.duration || 6000);

      return () => clearTimeout(timer);
    }
  }, [snackbar?.open]);

  const getAlertConfig = (severity) => {
    const configs = {
      success: {
        icon: <CheckCircle className="w-6 h-6" />,
        bgGradient: 'from-emerald-500 via-green-500 to-teal-600',
        bgLight: 'from-emerald-50/90 via-green-50/90 to-teal-50/90',
        borderColor: 'border-emerald-200/50',
        textColor: 'text-emerald-900',
        descColor: 'text-emerald-700',
        iconColor: 'text-emerald-600',
        progressColor: 'bg-gradient-to-r from-emerald-400 to-green-500',
        glowColor: 'shadow-emerald-500/30',
        ringColor: 'ring-emerald-500/20'
      },
      error: {
        icon: <AlertCircle className="w-6 h-6" />,
        bgGradient: 'from-red-500 via-rose-500 to-pink-600',
        bgLight: 'from-red-50/90 via-rose-50/90 to-pink-50/90',
        borderColor: 'border-red-200/50',
        textColor: 'text-red-900',
        descColor: 'text-red-700',
        iconColor: 'text-red-600',
        progressColor: 'bg-gradient-to-r from-red-400 to-rose-500',
        glowColor: 'shadow-red-500/30',
        ringColor: 'ring-red-500/20'
      },
      warning: {
        icon: <AlertTriangle className="w-6 h-6" />,
        bgGradient: 'from-amber-500 via-orange-500 to-yellow-600',
        bgLight: 'from-amber-50/90 via-orange-50/90 to-yellow-50/90',
        borderColor: 'border-amber-200/50',
        textColor: 'text-amber-900',
        descColor: 'text-amber-700',
        iconColor: 'text-amber-600',
        progressColor: 'bg-gradient-to-r from-amber-400 to-orange-500',
        glowColor: 'shadow-amber-500/30',
        ringColor: 'ring-amber-500/20'
      },
      info: {
        icon: <Info className="w-6 h-6" />,
        bgGradient: 'from-blue-500 via-indigo-500 to-purple-600',
        bgLight: 'from-blue-50/90 via-indigo-50/90 to-purple-50/90',
        borderColor: 'border-blue-200/50',
        textColor: 'text-blue-900',
        descColor: 'text-blue-700',
        iconColor: 'text-blue-600',
        progressColor: 'bg-gradient-to-r from-blue-400 to-indigo-500',
        glowColor: 'shadow-blue-500/30',
        ringColor: 'ring-blue-500/20'
      },
      premium: {
        icon: <Star className="w-6 h-6" />,
        bgGradient: 'from-purple-500 via-violet-500 to-indigo-600',
        bgLight: 'from-purple-50/90 via-violet-50/90 to-indigo-50/90',
        borderColor: 'border-purple-200/50',
        textColor: 'text-purple-900',
        descColor: 'text-purple-700',
        iconColor: 'text-purple-600',
        progressColor: 'bg-gradient-to-r from-purple-400 to-violet-500',
        glowColor: 'shadow-purple-500/30',
        ringColor: 'ring-purple-500/20'
      }
    };
    return configs[severity] || configs.success;
  };

  const getPositionClasses = (position) => {
    const positions = {
      'top-left': {
        container: 'top-6 left-6',
        animation: isVisible && !isExiting 
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100' 
          : '-translate-x-4 -translate-y-4 opacity-0 scale-95'
      },
      'top-center': {
        container: 'top-6 left-1/2 transform -translate-x-1/2',
        animation: isVisible && !isExiting 
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100' 
          : 'translate-x-0 -translate-y-4 opacity-0 scale-95'
      },
      'top-right': {
        container: 'top-6 right-6',
        animation: isVisible && !isExiting 
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100' 
          : 'translate-x-4 -translate-y-4 opacity-0 scale-95'
      },
      'bottom-left': {
        container: 'bottom-6 left-6',
        animation: isVisible && !isExiting 
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100' 
          : '-translate-x-4 translate-y-4 opacity-0 scale-95'
      },
      'bottom-center': {
        container: 'bottom-6 left-1/2 transform -translate-x-1/2',
        animation: isVisible && !isExiting 
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100' 
          : 'translate-x-0 translate-y-4 opacity-0 scale-95'
      },
      'bottom-right': {
        container: 'bottom-6 right-6',
        animation: isVisible && !isExiting 
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100' 
          : 'translate-x-4 translate-y-4 opacity-0 scale-95'
      },
      'center': {
        container: 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
        animation: isVisible && !isExiting 
          ? 'translate-x-0 translate-y-0 opacity-100 scale-100' 
          : 'translate-x-0 translate-y-0 opacity-0 scale-90'
      }
    };
    return positions[position] || positions['top-center'];
  };

  if (!snackbar?.open && !isVisible) return null;

  const config = getAlertConfig(snackbar?.severity || 'success');
  const positionConfig = getPositionClasses(snackbar?.position || 'top-center');

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Toast Container */}
      <div className={`absolute ${positionConfig.container} w-[600px]`}>
        <div
          className={`
            transform transition-all duration-400 ease-out pointer-events-auto
            ${positionConfig.animation}
          `}
        >
          {/* Main Alert Card */}
          <div className={`
            relative overflow-hidden rounded-3xl 
            bg-gradient-to-br ${config.bgLight}
            border-2 ${config.borderColor}
            backdrop-blur-2xl
            shadow-2xl ${config.glowColor}
            ring-1 ${config.ringColor}
            group
            hover:shadow-3xl hover:scale-[1.02]
            transition-all duration-300
          `}>
            
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3Ccircle cx='10' cy='10' r='1'/%3E%3Ccircle cx='50' cy='10' r='1'/%3E%3Ccircle cx='10' cy='50' r='1'/%3E%3Ccircle cx='50' cy='50' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>

            {/* Floating particles effect */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-4 right-8 w-2 h-2 bg-white/20 rounded-full animate-pulse" />
              <div className="absolute top-8 right-12 w-1 h-1 bg-white/15 rounded-full animate-ping" style={{ animationDelay: '1s' }} />
              <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
            </div>

            {/* Premium Glass Border Effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />

            {/* Content */}
            <div className="relative p-6">
              <div className="flex items-start gap-4">
                {/* Icon with premium styling */}
                <div className={`
                  flex-shrink-0 w-12 h-12 rounded-2xl
                  bg-gradient-to-br ${config.bgGradient}
                  flex items-center justify-center
                  shadow-xl ring-2 ring-white/30
                  group-hover:scale-110 group-hover:rotate-3
                  transition-all duration-300
                  relative overflow-hidden
                `}>
                  {/* Icon shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-700" />
                  <div className="text-white relative z-10">
                    {config.icon}
                  </div>
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      {/* Main Message */}
                      <h4 className={`
                        font-bold text-base leading-tight
                        ${config.textColor}
                      `}>
                        {snackbar?.message}
                      </h4>
                      
                      {/* Description */}
                      {snackbar?.description && (
                        <p className={`
                          text-sm leading-relaxed
                          ${config.descColor}
                          opacity-90
                        `}>
                          {snackbar.description}
                        </p>
                      )}
                    </div>

                    {/* Close Button */}
                    <button
                      onClick={handleClose}
                      className={`
                        flex-shrink-0 w-10 h-10 rounded-2xl
                        flex items-center justify-center
                        ${config.iconColor}
                        hover:bg-white/20 active:bg-white/30
                        transition-all duration-200
                        group/close
                        hover:scale-110 active:scale-95
                      `}
                    >
                      <X className="w-5 h-5 group-hover/close:rotate-90 transition-transform duration-200" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {snackbar?.actions && snackbar.actions.length > 0 && (
                <div className="mt-6 flex gap-3 justify-end">
                  {snackbar.actions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.onClick}
                      className={`
                        px-5 py-2.5 rounded-2xl text-sm font-semibold
                        ${action.variant === 'outline' 
                          ? `border-2 ${config.borderColor} ${config.textColor} hover:bg-white/20`
                          : `bg-gradient-to-r ${config.bgGradient} text-white shadow-lg`
                        }
                        hover:shadow-xl hover:scale-105
                        active:scale-95
                        transition-all duration-200
                        flex items-center gap-2
                        backdrop-blur-sm
                      `}
                    >
                      {action.icon && (
                        <span className="w-4 h-4">
                          {action.icon}
                        </span>
                      )}
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Premium Progress Bar */}
            <div className="relative h-1.5 bg-black/5">
              <div 
                className={`
                  absolute left-0 top-0 h-full rounded-full
                  ${config.progressColor}
                  shadow-md
                  animate-[shrink_${snackbar?.duration || 6000}ms_linear_forwards]
                  relative overflow-hidden
                `}
                style={{ width: '100%' }}
              >
                {/* Progress bar shimmer */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 translate-x-full animate-[shimmer_2s_infinite]" />
              </div>
            </div>

            {/* Premium shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000" />
          </div>
        </div>
      </div>

      {/* Enhanced Styles */}
      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }

        .shadow-3xl {
          box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </div>
  );
};

export default SnackContainer;